// Presentation helpers for a prospect's stations (CWA-94). Kept out of
// onboardingStates.ts because these describe a station, not the prospect's
// place in the pipeline.

import { ProspectStation } from "../types/onboarding";

/**
 * Where a station stands, in words an operations agent reads without training.
 *
 * Falls back to the access-data flag when `status` is absent, so a response from
 * a backend older than the go-live release still renders sensibly.
 */
export const stationStatusLabel = (station: ProspectStation): string => {
  const status =
    station.status ??
    (station.has_credentials ? "CREDENTIALS_CONFIGURED" : "PENDING");

  switch (status) {
    case "LIVE":
      return "Live";
    case "CREDENTIALS_CONFIGURED":
      return "Ready, not yet live";
    default:
      return "Waiting for access data";
  }
};

// PrimeReact Tag severity for the same three cases.
export const stationStatusSeverity = (
  station: ProspectStation
): "success" | "info" | "warning" => {
  const status =
    station.status ??
    (station.has_credentials ? "CREDENTIALS_CONFIGURED" : "PENDING");

  if (status === "LIVE") return "success";
  if (status === "CREDENTIALS_CONFIGURED") return "info";
  return "warning";
};

/**
 * Label for one station activity entry, or null for a type this screen does not
 * know. Returning null rather than the raw type keeps a future backend event
 * from printing an identifier at an agent (FR-017).
 *
 * `repeat` marks a `LIVE` entry that is not the first one. The backend derives
 * these from the go-live audit trail, so every resend of the confirmation email
 * adds another `LIVE` entry — and a station that reads "Station went live"
 * three times tells an agent something that never happened.
 */
export const stationEventLabel = (
  type: string,
  repeat = false
): string | null => {
  switch (type) {
    case "CREDENTIALS_SAVED":
      return "Access data recorded";
    case "LIVE":
      return repeat ? "Confirmation email sent again" : "Station went live";
    default:
      return null;
  }
};
